import { ChangeEvent, useCallback } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { useIntl } from 'react-intl';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import { ExtendedUIState } from '@sorare/core/src/components/search/InstantSearch/types';
import { Switch } from '@sorare/core/src/components/search/Switch';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import useVirtualToggle from '@sorare/core/src/hooks/useVirtualToggle';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import {
  FilterWidget,
  VIRTUAL_TOGGLE_FILTERS,
  VirtualToggleFilter,
} from '@sorare/core/src/lib/filters';
import { filters } from '@sorare/core/src/lib/glossary';

const NON_PLAYABLE_CARDS_FILTER = VIRTUAL_TOGGLE_FILTERS.nonPlayableCardsFilter;
const LEGEND_FILTER = VIRTUAL_TOGGLE_FILTERS.legendFilter;

type SharedProps = {
  withCustomSeries: boolean;
  withLegend: boolean;
};

const Filter = ({ filter }: { filter: VirtualToggleFilter }) => {
  const { formatMessage } = useIntl();
  const track = useEvents();
  const setVirtualToggle = useVirtualToggleManager();
  const { currentRefinement } = useVirtualToggle<boolean>({
    name: filter.name,
  });

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked;
      setVirtualToggle({
        [filter.name]: newValue,
      });
      track('Use Market Filter', {
        filterName: filter.trackingName,
        filterValue: newValue.toString(),
      });
    },
    [filter, setVirtualToggle, track]
  );

  return (
    <Switch
      checked={!!currentRefinement}
      onChange={handleChange}
      label={<LabelM>{formatMessage(filter.label!)}</LabelM>}
    />
  );
};

type FiltersProps = SharedProps & {
  visible: boolean;
};

const Filters = ({ withCustomSeries, withLegend, visible }: FiltersProps) => {
  const {
    flags: { useLegendMarketplaceFilter = false },
  } = useFeatureFlags();

  if (!visible) return <FilterSection />;

  return (
    <Vertical>
      {[
        ...(withCustomSeries ? [NON_PLAYABLE_CARDS_FILTER] : []),
        ...(useLegendMarketplaceFilter && withLegend ? [LEGEND_FILTER] : []),
      ].map(filter => (
        <Filter key={filter.name} filter={filter} />
      ))}
    </Vertical>
  );
};

const Title = ({ withCustomSeries, withLegend }: SharedProps) => {
  const { indexUiState } = useInstantSearch<ExtendedUIState>();

  const selected =
    (withCustomSeries &&
    indexUiState.virtualToggle?.[NON_PLAYABLE_CARDS_FILTER.name]
      ? 1
      : 0) +
    (withLegend && indexUiState.virtualToggle?.[LEGEND_FILTER.name] ? 1 : 0);

  return (
    <FilterTitle name={filters.collectibleFilterTitle} selected={selected} />
  );
};

export default ({
  withCustomSeries = true,
  withLegend = true,
}: {
  withCustomSeries?: boolean;
  withLegend?: boolean;
} = {}): FilterWidget => ({
  key: 'collectible',
  type: 'virtualToggle',
  filter: NON_PLAYABLE_CARDS_FILTER,
  title: <Title withCustomSeries={withCustomSeries} withLegend={withLegend} />,
  Component: ({ visible }) => (
    <Filters
      withCustomSeries={withCustomSeries}
      withLegend={withLegend}
      visible={visible}
    />
  ),
});
